// TopSection.jsx
"use client";

import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import blackCar from "../../../assets/blackCar.webp";
import whiteCar from "../../../assets/whiteCar.avif";
import heroCar from "@/assets/home-hero-auto.webp";
import Konfetti from "@/assets/svgs/Konfetti";
import Konfetti2 from "@/assets/svgs/Konfetti2";
import ButtonAnimated from "../../../components/ButtonAnimated/ButtonAnimated";
import Image from "next/image";
import Underline from "../../../components/Underline/Underline";
import trackVehicleCreated from "@/lib/trackVehicleCreated";
import TrustPilot from "@/common/TrustPilot";
import Input from "@/common/Input";
import useBackendUrl from "@/app/helpers/useBackendUrl";
import useIsMobile from "@/app/helpers/useIsMobile";
import { useAuth } from "@/context/ContextProvider";
import { useRouter } from "next/navigation";
import { formatNumber } from "@/helpers/formatNumber";
import timerIcon from "@/assets/svgs/timer.svg";
import attentionIcon from "@/assets/svgs/attention.svg";
import BrandLogo from "./brandLogo";
import questionMarkIcon from "@/assets/icons/questionMark.svg";
import useFirstCompletedCar from "@/app/helpers/useFirstCompletedCarListener";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation";
import {
  checkVehicleSteps,
  getVehicleInfoFlowKeys,
  isAbFlowStarted,
} from "@/helpers/fetchVehicleSteps";
import { createVehicleFlow } from "@/app/helpers/vehicleStepFlow";
import Skeleton from "react-loading-skeleton";
import { saveAnmeldenFlowPayload } from "@/app/anmelden/anmeldenFlowPayload";
import { trackPreReg } from "@/app/helpers/tracking";
import { getPreRegSessionId, setPreRegSourceFlow } from "@/app/helpers/preRegSession";
import { getDisplayValuation } from "@/helpers/getDisplayValuation";

const hasPositivePrice = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "number") return Number.isFinite(value) && value > 0;
  const numeric = Number(String(value).trim().replace(/[^\d.-]/g, ""));
  return Number.isFinite(numeric) && numeric > 0;
};

/* ------------------------- CarTopInfo ------------------------- */
function CarTopInfo({
  items,
  textNode,
  shouldAnimate,
  animateOut,
  animateIn,
  hidden,
}) {
  const [showChips, setShowChips] = React.useState(items?.length > 0);
  const [snapshot, setSnapshot] = React.useState(items || []);

  React.useEffect(() => {
    if (items?.length > 0) {
      setSnapshot(items);
      setShowChips(true);
      return;
    }
    // items leer -> der Subtitle-Text soll erscheinen.
    // WICHTIG (Codex-Fix): den aktuellen Inhalt (Chip-Snapshot) während JEDES
    // aktiven Fade-Outs behalten – unabhängig von shouldAnimate. Beim "Zurück"
    // löscht getBackStep die Marke (items=[]) BEVOR nextStepRef/shouldAnimateInfo
    // gesetzt sind; ohne diesen Guard würde der Subtitle hier bei voller Deckkraft
    // ohne fade-out/is-hidden rendern -> der "kommt -> weg -> zurück"-Snap.
    if (animateOut && !hidden) return;
    setShowChips(false);
  }, [items, animateOut, hidden]);

  return (
    <div
      className={[
        "Car-Top-Info",
        shouldAnimate && animateOut ? "fade-out" : "",
        shouldAnimate && animateIn ? "fade-in" : "",
        hidden ? "is-hidden" : "", // 👈 neu
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showChips ? <SelectionChips items={snapshot} /> : textNode}
    </div>
  );
}

/* ---------------------- SelectionChips ----------------------- */
function SelectionChips({ items }) {
  const isInitialMount = useRef(true);
  const EXIT_MS = 280;

  const [chipMap, setChipMap] = useState(() => {
    const m = new Map();
    (items ?? []).forEach((i) => m.set(i.key, { item: i, status: "idle" }));
    return m;
  });

  const TOP_KEYS = useMemo(() => new Set(["brand", "model", "var", "mt"]), []);

  const formatValue = (key, value) => {
    if (value == null) return "";
    const str = String(value).trim();

    if (key === "doors") {
      const n = Number(str);
      return Number.isFinite(n) ? `${n} Türen` : `${str} Türen`;
    }
    if (key === "ps") {
      const psMatch = str.match(/(\d+)\s*PS/i);
      if (psMatch) return `${psMatch[1]} PS`;
      const kwMatch = str.match(/(\d+)\s*kW/i);
      if (kwMatch) {
        const kw = parseInt(kwMatch[1], 10);
        if (Number.isFinite(kw)) {
          const ps = Math.round(kw * 1.35962);
          return `${ps} PS`;
        }
      }
      if (/^\d+$/.test(str)) return `${str} PS`;
      return str;
    }
    return str;
  };

  useEffect(() => {
    const newKeys = new Set(items?.map((i) => i.key) ?? []);
    setChipMap((prev) => {
      const next = new Map(prev);

      (items ?? []).forEach((it) => {
        const got = next.get(it.key);
        if (!got) {
          next.set(it.key, {
            item: it,
            status: isInitialMount.current ? "idle" : "enter",
          });
        } else {
          next.set(it.key, {
            item: it,
            status: got.status === "exit" ? "exit" : "idle",
          });
        }
      });

      for (const [key, val] of next) {
        if (!newKeys.has(key) && val.status !== "exit") {
          next.set(key, { ...val, status: "exit" });
          setTimeout(() => {
            setChipMap((curr) => {
              const c = new Map(curr);
              const v = c.get(key);
              if (!v || v.status !== "exit") return curr;
              c.delete(key);
              return c;
            });
          }, EXIT_MS);
        }
      }
      return next;
    });

    isInitialMount.current = false;
  }, [items]);

  if ((items?.length ?? 0) === 0 && chipMap.size === 0) return null;

  const all = Array.from(chipMap.values());
  const top = all.filter(({ item }) => TOP_KEYS.has(item.key));
  const bottom = all.filter(({ item }) => !TOP_KEYS.has(item.key));

  const renderChip = ({ item, status }, idx) => {
    const cls = [
      "chip",
      status === "enter" ? "chip--enter" : "",
      status === "exit" ? "chip--exit" : "",
    ]
      .filter(Boolean)
      .join(" ");
    const style =
      status === "exit"
        ? { animationDuration: `${EXIT_MS}ms`, animationDelay: `${idx * 20}ms` }
        : undefined;

    return (
      <span
        className={cls}
        role="listitem"
        key={item.key}
        data-row={TOP_KEYS.has(item.key) ? "top" : "bottom"}
        style={style}
      >
        <span className="chip-value">{formatValue(item.key, item.value)}</span>
      </span>
    );
  };

  return (
    <div className="SelectedChips" role="list">
      <div
        className="SelectedChipsRow SelectedChipsRow--top"
        role="group"
        aria-label="Auswahl – Hauptangaben"
      >
        {top.map(renderChip)}
      </div>
      <div
        className="SelectedChipsRow SelectedChipsRow--bottom"
        role="group"
        aria-label="Auswahl – Details"
      >
        {bottom.map(renderChip)}
      </div>
    </div>
  );
}

/* -------------------------- TopSection -------------------------- */
export const TopSection = ({
  step,
  setStep,
  setSelectedBrand,
  selectedBrand,
  setSelectedModel,
  selectedModel,
  setSelectedYear,
  selectedYear,
  setSelectedBauform,
  selectedBauform,
  setSelectedFuel,
  selectedFuel,
  setSelectedVariant,
  selectedVariant,
  setSelectedPs,
  selectedPs,
  setSelectedTransmission,
  selectedTransmission,
  setSelectedDoors,
  selectedDoors,
  setSelectedModelType,
  selectedModelType,
  setSelectedKmStand,
  selectedKmStand,
  initialCarData = null,
}) => {
  const { userData } = useAuth();
  const api = useBackendUrl();
  const isMobile = useIsMobile();
  const router = useRouter();
  const showToastDeferred = React.useCallback((message, type = "error") => {
    import("@/app/helpers/showToast").then(({ showToast }) =>
      showToast(message, type),
    );
  }, []);

  useEffect(() => {
    if (step < 10) return;
    router.prefetch("/anmelden");
  }, [router, step]);

  // Animation / click guard
  const [animateOut, setAnimateOut] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const [isChevronAnimating, setIsChevronAnimating] = useState(false);
  const [isChevronLooping, setIsChevronLooping] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [inputError, setInputError] = useState("");
  // Fehlermeldung beim Step-Wechsel (vor/zurueck) zuruecksetzen, damit sie nicht
  // am falschen Feld haengen bleibt.
  React.useEffect(() => {
    setInputError("");
  }, [step]);
  const [swapInvisible, setSwapInvisible] = useState(false);
  const [lockFadeIn, setLockFadeIn] = useState(false);

  // Roh-Daten
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [bauformen, setBauformen] = useState([]);
  const [fuels, setFuels] = useState([]);
  const [variants, setVariants] = useState([]);
  const [psOptions, setPsOptions] = useState([]);
  const [gearboxOptions, setGearboxOptions] = useState([]);
  const [doorOptions, setDoorOptions] = useState([]);
  const [modelTypeOptions, setModelTypeOptions] = useState([]);

  // Inputs
  const [brandInput, setBrandInput] = useState("");
  const [modelInput, setModelInput] = useState("");
  const [yearInput, setYearInput] = useState("");
  const [bauformInput, setBauformInput] = useState("");
  const [fuelInput, setFuelInput] = useState("");
  const [variantInput, setVariantInput] = useState("");
  const [psInput, setPsInput] = useState("");
  const [transmissionInput, setTransmissionInput] = useState("");
  const [doorsInput, setDoorsInput] = useState("");
  const [modelTypeInput, setModelTypeInput] = useState("");
  const [kmStandInput, setKmStandInput] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [filteredItemsStep, setFilteredItemsStep] = useState(0);

  const setFilteredForStep = (items, stepIndex) => {
    setFilteredItems(items);
    setFilteredItemsStep(stepIndex);
  };

  const hasUserFins = Array.isArray(userData?.fins) && userData.fins.length > 0;
  const [shouldLoadCarData, setShouldLoadCarData] = useState(() => !hasUserFins);

  useEffect(() => {
    if (typeof window === "undefined") return;

    if (!hasUserFins) {
      setShouldLoadCarData(true);
      return;
    }

    setShouldLoadCarData(false);

    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      setShouldLoadCarData(true);
    };

    // Firestore-SDK + Reads bleiben aus dem synchronen Initial-Render heraus
    // (TBT/LCP-Schutz), werden aber direkt nach dem First Paint via Idle geladen
    // statt nach einem langen Fallback. Frueher: setTimeout(start, 6000) -> ein
    // passiver User sah das Skeleton bis zu 6s. Jetzt: requestIdleCallback (laedt
    // typ. wenige 100ms nach Paint) mit kurzem setTimeout-Fallback (<=700ms);
    // Interaktion laedt weiterhin sofort.
    const events = ["pointerdown", "keydown", "touchstart"];
    const options = { passive: true };
    events.forEach((evt) => {
      window.addEventListener(evt, start, options);
    });

    const idleId =
      typeof window.requestIdleCallback === "function"
        ? window.requestIdleCallback(start, { timeout: 1200 })
        : null;
    const fallbackTimer = window.setTimeout(start, 700);

    return () => {
      window.clearTimeout(fallbackTimer);
      if (idleId != null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleId);
      }
      events.forEach((evt) => {
        window.removeEventListener(evt, start, options);
      });
    };
  }, [hasUserFins]);

  const { carData, loading } = useFirstCompletedCar(userData?.fins, {
    enabled: shouldLoadCarData,
    initialData: initialCarData,
  });

  // Referenz-Style Preis-Reveal: Der Preis steht bereits im (SSR-)HTML unter
  // einem Blur-Overlay; kurz nach Hydration/Datenankunft wird er "enthüllt".
  const [priceRevealed, setPriceRevealed] = useState(false);
  useEffect(() => {
    if (!carData || loading) return undefined;
    const t = window.setTimeout(() => setPriceRevealed(true), 300);
    return () => window.clearTimeout(t);
  }, [carData, loading]);

  const steps = useMemo(() => {
    if (!carData || !userData) return {};
    return checkVehicleSteps(userData, carData) || {};
  }, [carData, userData]);
  const progress = steps.progress ?? 0;
  const status = steps.status ?? {};
  const vehicleInformationDone = steps.vehicleInformationDone ?? false;
  const computedProgress =
    typeof progress === "number" && Number.isFinite(progress)
      ? Math.max(0, Math.min(100, Math.round(progress)))
      : 0;
  const isPriceCalculationAutomated =
    carData?.ab?.priceCalculationAutomated === true ||
    carData?.priceCalculationAutomated === true;
  const isSelfValuationFlow = carData?.ab?.selfValuation === true;
  const abFlowStarted = isAbFlowStarted(carData || {});

  const arePhotosDone =
    !!status?.exteriorPhotosDone?.done &&
    !!status?.interiorPhotosDone?.done &&
    !!status?.rimsPhotosDone?.done &&
    !!status?.wheelsPhotosDone?.done;
  const damagesDone = !!status?.damagesDone?.done;
  const serviceDone = !!status?.serviceHistoryDone?.done;
  const allStepsDone =
    (vehicleInformationDone && arePhotosDone && damagesDone && serviceDone) ||
    (!isPriceCalculationAutomated && progress >= 100);

  const c = carData?.confirmInformation || {};
  const allConfirmed =
    c.personalInformation === true &&
    c.vehicleInformation === true &&
    c.photos === true &&
    c.damages === true &&
    (c.serviceHistory === true || c.service === true);

  const hasMinPrice =
    carData?.minPrice !== undefined &&
    carData?.minPrice !== null &&
    String(carData?.minPrice).trim() !== "";
  const hasPrice =
    carData?.price !== undefined &&
    carData?.price !== null &&
    String(carData?.price).trim() !== "";
  // Angezeigter Schätzpreis: EK/Ankaufswert bevorzugt, Fallback Marktwert.
  // hasPrice-Guard bleibt bewusst auf carData.price.
  const displayEstimate = getDisplayValuation(carData).price;
  const hasFinalPrice =
    carData?.auction?.finalPrice !== undefined &&
    carData?.auction?.finalPrice !== null &&
    String(carData?.auction?.finalPrice).trim() !== "";
  const hasAuctionStatus = Boolean(carData?.auction?.sellStatus);
  // Beim Weitermachen immer zuerst auf die Preis-Seite, sobald ein GESCHAETZTER
  // Preis (carData.price) vorliegt – NICHT bei Richtpreis/Mindestpreis
  // (minPrice). Verkaufte/Auktions-Autos sind ausgeschlossen. isCompleted bleibt
  // als Guard: nur fertig bewertete Autos haben einen geschaetzten Preis, und es
  // verhindert die /status-Weiterleitung der Preis-Seite fuer Nicht-Abgeschlossene.
  const shouldRouteToPreis =
    carData?.isCompleted === true &&
    hasPrice &&
    !hasFinalPrice &&
    !hasAuctionStatus;
  const preisRoute = `/preis/${carData?.id}`;
  const profileRoute = shouldRouteToPreis
    ? preisRoute
    : `/${carData?.id}/profil`;
  const confirmPriceRoute = `/${carData?.id}/profil/kaufpreis-bestaetigen`;
  const vehicleInfoFlowKeys = getVehicleInfoFlowKeys(carData || {}, userData);
  const allVehicleInfoQuestionsDone = abFlowStarted
    ? vehicleInfoFlowKeys.every((k) => status[k]?.done)
    : false;

  const showPercentage =
    isPriceCalculationAutomated && !hasMinPrice && !hasPrice && !hasFinalPrice;
  const hasNoCalculatedPrice = !hasMinPrice && !hasPrice;
  const waitingForValuation =
    isPriceCalculationAutomated &&
    carData?.isCompleted !== true &&
    !hasMinPrice &&
    !hasPrice &&
    !hasFinalPrice &&
    computedProgress >= 100;
  const vehiclePriceTooltipText = hasNoCalculatedPrice
    ? "Die Bewertung erfolgt nach der Erstellung eines Fahrzeugprofils."
    : waitingForValuation
      ? "Dein geschätzter Verkaufspreis wird anhand der aktuellen Marktdaten berechnet"
      : showPercentage
        ? "Ergänze die fehlenden Informationen, um den geschätzten Verkaufspreis zu erhalten"
        : "Basierend auf Echtzeit-Marktwerten und Daten von über 5.000 geprüften Händlern.";

  const getMs = (ts) => {
    if (!ts) return null;
    if (typeof ts.toMillis === "function") return ts.toMillis();
    if (typeof ts === "number") return ts;
    if (typeof ts === "string") {
      const parsed = Date.parse(ts);
      return Number.isNaN(parsed) ? null : parsed;
    }
    if (typeof ts === "object") {
      const seconds = ts.seconds ?? ts._seconds;
      if (typeof seconds === "number") return seconds * 1000;
    }
    return null;
  };

  const valuationCreatedAtMs =
    getMs(carData?.valuationCreatedAt) ?? getMs(carData?.createdAt);
  const SEVEN_DAYS = 7 * 24 * 60 * 60 * 1000;
  // Ein stabiler Zeit-Tick statt Date.now() bei jedem Render: minimiert
  // SSR/Hydration-Differenzen (Karte wird jetzt server-gerendert) und wird
  // nach dem Mount einmal auf Client-Zeit nachgezogen.
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    setNowTick(Date.now());
  }, []);
  const now = nowTick;

  const isFahrzeugProfilDone = carData?.fahrzeugProfilDone || false;
  const isFahrzeugProfileDoneBackup =
    carData?.status === "new" ||
    carData?.status === "finished" ||
    carData?.status === "approved" ||
    carData?.status === "data-reviewed" ||
    carData?.sellStatus === "cancelled" ||
    carData?.status === "extended";

  const isWithin7DaysWindow =
    valuationCreatedAtMs != null &&
    now >= valuationCreatedAtMs &&
    now <= valuationCreatedAtMs + SEVEN_DAYS;

  const isActiveNotExpired =
    isFahrzeugProfilDone || isWithin7DaysWindow || isFahrzeugProfileDoneBackup;
  const effectiveIsActiveNotExpired = isSelfValuationFlow
    ? true
    : isActiveNotExpired;
  const selfValuationHasReauctioned =
    Number(carData?.reauctionTracking?.selfInitiatedCount || 0) > 0 ||
    String(carData?.reauctionTracking?.lastOutcome || "")
      .trim()
      .toLowerCase() === "self_reauctioned";
  const isSoldState =
    carData?.auction?.sellStatus === "price-confirmed" ||
    carData?.auction?.sellStatus === "sold";
  const isNoBidsWithoutReauction =
    carData?.auction?.sellStatus === "no-bids" && !selfValuationHasReauctioned;
  const isCancelledState =
    carData?.status === "not-interested" ||
    carData?.auction?.sellStatus === "cancelled";
  const isVehicleProfileFinished =
    carData?.fahrzeugProfilDone === true ||
    carData?.fahrzeugprofileDone === true ||
    carData?.confirmBuyPrice === true ||
    (allStepsDone && allConfirmed);
  const selfValuationCta = useMemo(() => {
    if (!carData?.id) return { text: "Fortfahren", link: "/account" };
    if (!isVehicleProfileFinished) {
      return { text: "Fortfahren", link: profileRoute };
    }
    if (isSoldState) {
      return {
        text: "Verkauft",
        link: `/${carData.id}/profil/verkauf-bestaetigt`,
      };
    }
    if (isNoBidsWithoutReauction) {
      return {
        text: "Keine Gebote",
        link: `/${carData.id}/profil/keine-gebote`,
      };
    }
    if (isCancelledState) {
      return {
        text: "Storniert",
        link: `/${carData.id}/profil/verkauf-storniert`,
      };
    }
    return {
      text: "Fortfahren",
      link: `/${carData.id}/profil/naechste-schritte`,
    };
  }, [
    carData?.id,
    isVehicleProfileFinished,
    profileRoute,
    isSoldState,
    isNoBidsWithoutReauction,
    isCancelledState,
  ]);

  const statusValue = String(carData?.status ?? "").trim();
  const hasStatusOverride =
    !effectiveIsActiveNotExpired ||
    Boolean(carData?.auction?.sellStatus) ||
    (statusValue.length > 0 && statusValue !== "reset") ||
    carData?.confirmBuyPrice === true ||
    (allStepsDone && !allConfirmed) ||
    hasFinalPrice;

  /* ---- Imperative Orchestrierung (ohne Effects) ---- */
  const nextStepRef = useRef(null); // Ziel-Step
  const fadeOutDoneRef = useRef(false); // Fade-Out abgeschlossen?
  const swapInvisibleRef = useRef(false); // is-hidden Status
  const chevronDoneRef = useRef(false);
  const pendingRouteRef = useRef("");
  const didNavigateRef = useRef(false);
  const guestRedirectTimerRef = useRef(null);

  useEffect(() => {
    swapInvisibleRef.current = swapInvisible;
  }, [swapInvisible]);

  const clearGuestRedirectTimer = () => {
    if (!guestRedirectTimerRef.current) return;
    window.clearTimeout(guestRedirectTimerRef.current);
    guestRedirectTimerRef.current = null;
  };

  useEffect(
    () => () => {
      clearGuestRedirectTimer();
    },
    [],
  );

  // Map: welcher Options-Array gehört zu welchem Step
  const optionsByStep = useMemo(
    () => ({
      1: brands,
      2: models,
      3: years,
      4: bauformen,
      5: fuels,
      6: variants,
      7: psOptions,
      8: gearboxOptions,
      9: doorOptions,
      10: modelTypeOptions,
    }),
    [
      brands,
      models,
      years,
      bauformen,
      fuels,
      variants,
      psOptions,
      gearboxOptions,
      doorOptions,
      modelTypeOptions,
    ],
  );

  // Step ist „UI-ready“, wenn er 11 ist oder seine Options > 1 haben
  const isStepUIReady = useCallback(
    (s) => {
      if (s == null) return false;
      if (s === 11) return true;
      const opts = optionsByStep[s] || [];
      return opts.length !== 1; // 0 -> ok, >=2 -> ok, ==1 -> Auto-Skip, noch nicht zeigen
    },
    [optionsByStep],
  );

  // ✅ Commit eines Ziel-Steps inkl. späterem Fade-In (ohne RAF-Polling)
  const ensureFadeInFor = (targetStep) => {
    if (targetStep == null) return;

    // 1) Step wechseln + erstmal unsichtbar schalten
    setStep(targetStep);
    setSwapInvisible(true);

    // 2) Nur merken; das Fade-In wird vom Gatekeeper-Effect ausgelöst,
    //    sobald die UI wirklich bereit ist (siehe Effect unten).
    nextStepRef.current = targetStep;
  };

  // ✅ NEU: Gatekeeper-Effect, der das Fade-In auslöst, sobald Daten/State bereit sind
  useEffect(() => {
    const target = nextStepRef.current;
    if (!target) return; // kein Ziel? raus
    if (!swapInvisible) return; // nur reagieren, wenn wir gerade versteckt sind
    if (lockFadeIn) return; // submit animation active -> never fade back in
    if (step !== target) return; // erst einblenden, wenn der Ziel-Step gesetzt ist
    if (!isStepUIReady(target)) return; // noch nicht bereit? warten

    // 🎉 Jetzt einblenden
    setAnimateIn(true);
    setSwapInvisible(false);

    // Aufräumen
    nextStepRef.current = null;
    fadeOutDoneRef.current = false;
  }, [swapInvisible, lockFadeIn, isStepUIReady, step]);

  /* ---- Button Titel/Link Logik ----
     Synchron (useMemo statt useEffect), damit der korrekte CTA bereits im
     SSR-HTML steht und nach der Hydration kein Text-/Link-Swap sichtbar ist. */
  const cta = useMemo(() => {
    if (!carData) {
      return { title: "Fahrzeugprofil starten", link: "/" };
    }

    const statusCta = () => {
      if (isSelfValuationFlow) {
        return { title: selfValuationCta.text, link: selfValuationCta.link };
      }

      if (!effectiveIsActiveNotExpired) {
        return {
          title: "Bewertung aktualisieren",
          link: `/${carData?.id}/profil/bewertung-aktualisieren`,
        };
      }

      if (
        carData?.auction?.sellStatus === "price-confirmed" ||
        carData?.auction?.sellStatus === "sold"
      ) {
        return {
          title: "Verkauft",
          link: `/${carData?.id}/profil/verkauf-bestaetigt`,
        };
      }

      if (carData?.status === "not-interested") {
        return {
          title: "Storniert",
          link: `/${carData?.id}/profil/verkauf-storniert`,
        };
      }

      if (carData?.auction?.sellStatus === "cancelled") {
        return {
          title: "Storniert",
          link: `/${carData?.id}/profil/verkauf-storniert`,
        };
      }

      if (hasFinalPrice) {
        return {
          title: "Angebot ansehen",
          link: `/${carData?.id}/profil/naechste-schritte`,
        };
      }

      if (carData?.status === "data-reviewed") {
        return {
          title: "Nächste Schritte",
          link: `/${carData?.id}/profil/naechste-schritte`,
        };
      }

      if (carData?.status === "approved") {
        return {
          title: "Fortschritt ansehen",
          link: `/${carData?.id}/profil/naechste-schritte`,
        };
      }

      if (progress < 7) {
        return { title: "Fahrzeugprofil erstellen", link: profileRoute };
      }

      if (progress > 7 && progress < 100) {
        return { title: "Verkauf fortsetzen", link: profileRoute };
      }

      if (carData?.confirmBuyPrice) {
        return {
          title: "Nächste Schritte",
          link: `/${carData?.id}/profil/naechste-schritte`,
        };
      }

      if (allStepsDone && !allConfirmed) {
        return {
          title: "Angaben überprüfen",
          link: `/${carData?.id}/profil/profil-bestaetigen`,
        };
      }

      // Liegt ein geschätzter Preis vor (isCompleted + price, noch kein
      // Finalpreis/Auktionsstatus), zuerst auf die Preis-Seite – gleiche
      // Regel wie profileRoute oben; sonst direkt zur Kaufpreis-Bestätigung.
      if (shouldRouteToPreis) {
        return { title: "Verkaufspreis ansehen", link: preisRoute };
      }
      return { title: "Verkaufspreis bestätigen", link: confirmPriceRoute };
    };

    if (hasStatusOverride) {
      return statusCta();
    }

    // ✅ AB / incomplete flow
    if (isPriceCalculationAutomated || !carData?.isCompleted) {
      if (isSelfValuationFlow) {
        return { title: selfValuationCta.text, link: selfValuationCta.link };
      }
      if (isPriceCalculationAutomated) {
        const shouldGoToStatus = abFlowStarted
          ? allVehicleInfoQuestionsDone
          : allStepsDone;
        if (shouldGoToStatus) {
          if (carData?.isCompleted) {
            return { title: "Verkauf fortsetzen", link: profileRoute };
          }
          return { title: "Status überprüfen", link: `/status/${carData?.id}` };
        }
        return { title: "Verkauf fortsetzen", link: profileRoute };
      }
      return { title: "Status Überprüfen", link: `/status/${carData?.id}` };
    }

    return statusCta();
  }, [
    carData,
    hasStatusOverride,
    isSelfValuationFlow,
    selfValuationCta.text,
    selfValuationCta.link,
    isPriceCalculationAutomated,
    abFlowStarted,
    profileRoute,
    confirmPriceRoute,
    allVehicleInfoQuestionsDone,
    effectiveIsActiveNotExpired,
    progress,
    allStepsDone,
    allConfirmed,
    hasFinalPrice,
    shouldRouteToPreis,
    preisRoute,
  ]);
  const buttonTitle = cta.title;
  const buttonLink = cta.link;

  const [addVehicle, setAddVehicle] = useState(false);

  const timeLeft = useMemo(() => {
    const offer = carData?.auction?.offerValidUntil;
    if (offer) {
      // Auch server-serialisierte Timestamps ({_seconds}/{seconds}) abdecken,
      // sonst ergibt new Date(object) ein Invalid Date.
      const offerSecs = offer?.seconds ?? offer?._seconds;
      const expiresAt =
        offer instanceof Date
          ? offer
          : typeof offer.toDate === "function"
            ? offer.toDate()
            : typeof offerSecs === "number"
              ? new Date(offerSecs * 1000)
              : new Date(offer);
      const diffMs = expiresAt.getTime() - now;
      if (diffMs <= 0) return "Deine Bewertung ist abgelaufen";
      const hours = Math.ceil(diffMs / (1000 * 60 * 60));
      return `Läuft ab in ${hours} Stunden`;
    }
    if (!carData?.isCompleted || !carData.valuationCreatedAt) return "";
    // getMs deckt Timestamp-Objekte, {_seconds}, Strings und Numbers ab –
    // roher _seconds-Zugriff ergab bei String-Timestamps "NaN Tage".
    const createdAtMs = getMs(carData.valuationCreatedAt);
    if (createdAtMs == null) return "";
    const expiresAt = new Date(createdAtMs + 7 * 24 * 60 * 60 * 1000);
    const diffMs = expiresAt.getTime() - now;
    if (diffMs <= 0) return "Deine Bewertung ist abgelaufen";
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return days < 1 ? "Läuft ab Heute" : `Läuft ab in ${days} Tagen`;
  }, [carData, now]);

  const handleFadeOut = () => {
    setAnimateOut(true);
    setIsAnimating(true);
  };

  const onFadeOutEnd = (e) => {
    // only react to the wrapper's own fadeOut animation
    if (e.target !== e.currentTarget) return;
    const name = e.animationName || e.nativeEvent?.animationName;
    if (name !== "fadeOut") return;

    // 1) Hide immediately to prevent a one-frame flash
    setSwapInvisible(true);
    fadeOutDoneRef.current = true;

    // 2) Defer removal of the fade-out class to the next frame
    requestAnimationFrame(() => {
      setAnimateOut(false);

      if (nextStepRef.current != null) {
        ensureFadeInFor(nextStepRef.current);
      }
    });
  };

  const onFadeInEnd = (e) => {
    if (e.nativeEvent.animationName !== "fadeIn") return;
    setAnimateIn(false);
    setIsAnimating(false);
  };

  const optionSettersByStep = useMemo(
    () => [
      setBrands,
      setModels,
      setYears,
      setBauformen,
      setFuels,
      setVariants,
      setPsOptions,
      setGearboxOptions,
      setDoorOptions,
      setModelTypeOptions,
    ],
    [
      setBrands,
      setModels,
      setYears,
      setBauformen,
      setFuels,
      setVariants,
      setPsOptions,
      setGearboxOptions,
      setDoorOptions,
      setModelTypeOptions,
    ],
  );

  const allSetInputs = useMemo(
    () => [
      setBrandInput,
      setModelInput,
      setYearInput,
      setBauformInput,
      setFuelInput,
      setVariantInput,
      setPsInput,
      setTransmissionInput,
      setDoorsInput,
      setModelTypeInput,
      setKmStandInput,
    ],
    [
      setBrandInput,
      setModelInput,
      setYearInput,
      setBauformInput,
      setFuelInput,
      setVariantInput,
      setPsInput,
      setTransmissionInput,
      setDoorsInput,
      setModelTypeInput,
      setKmStandInput,
    ],
  );

  const allSetSelected = useMemo(
    () => [
      setSelectedBrand,
      setSelectedModel,
      setSelectedYear,
      setSelectedBauform,
      setSelectedFuel,
      setSelectedVariant,
      setSelectedPs,
      setSelectedTransmission,
      setSelectedDoors,
      setSelectedModelType,
      setSelectedKmStand,
    ],
    [
      setSelectedBrand,
      setSelectedModel,
      setSelectedYear,
      setSelectedBauform,
      setSelectedFuel,
      setSelectedVariant,
      setSelectedPs,
      setSelectedTransmission,
      setSelectedDoors,
      setSelectedModelType,
      setSelectedKmStand,
    ],
  );

  const vehicleFlow = useMemo(
    () =>
      createVehicleFlow({
        api,
        setOptionsByStep: optionSettersByStep,
        setInputsByStep: allSetInputs,
        setSelectedByStep: allSetSelected,
        setFilteredItems,
        showToast: showToastDeferred,
        alwaysSkipVehicleSteps: [10],
      }),
    [
      api,
      optionSettersByStep,
      allSetInputs,
      allSetSelected,
      setFilteredItems,
      showToastDeferred,
    ],
  );

  useEffect(() => {
    vehicleFlow.loadBrands();
  }, [vehicleFlow]);

  useEffect(() => {
    setFilteredForStep([], step);
  }, [step]);

  const tryNavigate = (route) => {
    if (!route || didNavigateRef.current) return;
    didNavigateRef.current = true;
    clearGuestRedirectTimer();
    router.push(route);
  };

  const handleChevronAnimationEnd = (event) => {
    const name = event?.animationName;
    if (name !== "moveLastChevron" && name !== "moveChevronMobile") return;
    if (chevronDoneRef.current) return;
    chevronDoneRef.current = true;
    if (pendingRouteRef.current) {
      tryNavigate(pendingRouteRef.current);
    }
  };

  const handleManualAdd = async (kmOverride) => {
    const km = (kmOverride ?? selectedKmStand ?? "").toString().trim();
    if (!km) {
      showToastDeferred("Kein Kilometerstand angegeben", "error");
      return;
    }
    if (km !== selectedKmStand) setSelectedKmStand(km);

    chevronDoneRef.current = false;
    pendingRouteRef.current = "";
    didNavigateRef.current = false;
    setIsChevronLooping(false);
    clearGuestRedirectTimer();

    const anmeldenPayload = {
      title: "Bitte gib deine E-Mail-Adresse ein, um fortzufahren",
      fin: null,
      isManual: true,
      marke: selectedBrand,
      model: selectedModel,
      baujahr: selectedYear,
      bodytype: selectedBauform,
      fueltype: selectedFuel,
      variant: selectedVariant,
      ps: selectedPs,
      gearbox: selectedTransmission,
      doors: selectedDoors,
      modelType: selectedModelType,
      kmStand: km,
      page: "Homepage",
    };

    if (userData) {
      setLockFadeIn(true);
      handleFadeOut();
    }

    setIsChevronAnimating(true);

    if (!userData) {
      setLockFadeIn(true);
      saveAnmeldenFlowPayload(anmeldenPayload);
      pendingRouteRef.current = "/anmelden?fromFlow=1";
      guestRedirectTimerRef.current = window.setTimeout(() => {
        tryNavigate("/anmelden?fromFlow=1");
      }, 3200);
      return;
    }

    const payload = {
      email: userData.email,
      kmStand: km,
      marke: selectedBrand.name,
      model: selectedModel.name,
      gearbox: selectedTransmission.name,
      fueltype: selectedFuel.name,
      bodytype: selectedBauform.name,
      baujahr: selectedYear.name,
      doors: selectedDoors.id,
      ps: selectedPs.name,
      variant: selectedVariant?.name ?? "",
      type: selectedModelType?.name ?? "",
      vehicleDataIds: {
        brand: selectedBrand?.id ?? null,
        model: selectedModel?.id ?? null,
        year: selectedYear?.id ?? null,
        body: selectedBauform?.id ?? null,
        fuel: selectedFuel?.id ?? null,
        variant: selectedVariant?.id ?? selectedVariant?.name ?? null,
        kw: selectedPs?.id ?? null,
        gearbox: selectedTransmission?.id ?? null,
        doors: selectedDoors?.id ?? null,
        modelType: selectedModelType?.id ?? null,
      },
      salesdate: "2025-06-01",
    };

    try {
      const backendRes = await fetch(`${api}/user/add-vehicle-manually`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const json = await backendRes.json();
      if (!backendRes.ok) throw new Error(json.error || "Fehler beim Anlegen");

      trackVehicleCreated({ vehicleId: json.id, entrypoint: "home" });

      const priceCalculationAutomated =
        json?.ab?.priceCalculationAutomated === true ||
        json?.priceCalculationAutomated === true;
      const routeWhenReady = priceCalculationAutomated
        ? `/status/${json.id}`
        : json.vehicleMatchFound
          ? `/preis/${json.id}`
          : `/status/${json.id}`;
      router.prefetch(routeWhenReady);
      pendingRouteRef.current = routeWhenReady;
      if (chevronDoneRef.current) {
        tryNavigate(routeWhenReady);
      }
    } catch (err) {
      setIsChevronAnimating(false);
      setIsChevronLooping(false);
      setLockFadeIn(false);
      setSwapInvisible(false);
      setAnimateOut(false);
      setIsAnimating(false);
      showToastDeferred(
        err.message === "Failed to fetch" ? "Netzwerkfehler" : err.message,
        "error",
      );
    }
  };

  /* ----------------- computeNextStep (Auto-Skip) ----------------- */
  const computeNextStep = async () =>
    vehicleFlow.getNextStep({
      step,
      selections: {
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedBauform,
        selectedFuel,
        selectedVariant,
        selectedPs,
        selectedTransmission,
        selectedDoors,
        selectedModelType,
      },
    });

  // --- helpers for input & selection (needed by JSX and autoskip) ---
  const brandAliases = { vw: "Volkswagen" };

  const handleSelect = (valObj, setInput, setSelectedObj, setFiltered) => {
    // keep input text in sync with the chosen option
    setInput(valObj?.name ?? "");
    // store the actual chosen object (id/name pair)
    setSelectedObj(valObj || { id: "", name: "" });
    // close dropdown
    setFiltered([]);
  };

  const handleInput = (
    val,
    options,
    setInput,
    setFiltered,
    setSelectedObj,
    currentStep,
  ) => {
    const raw = (val ?? "").trim();
    const lower = raw.toLowerCase();
    setInput(val);

    const list = Array.isArray(options) ? options : [];

    // brand alias (vw → Volkswagen) when typing in step 1
    let filtered;
    if (currentStep === 1 && lower === "vw") {
      filtered = list.filter(
        (o) => (o.name ?? "").toLowerCase() === brandAliases.vw.toLowerCase(),
      );
    } else {
      filtered = list.filter((o) =>
        (o.name ?? "").toLowerCase().startsWith(lower),
      );
    }
    setFiltered(filtered);

    // special case: step 11 is a plain numeric input (km), not an options list
    if (currentStep === 11) {
      setKmStandInput(val); // mirror to visible input
      setSelectedKmStand(raw); // trimmed value we submit
      return;
    }

    // if user typed an exact option, select it; else clear selection
    const exactMatch = list.find(
      (o) => String(o.name ?? "").toLowerCase() === lower,
    );

    if (exactMatch) {
      setSelectedObj(exactMatch);
    } else {
      setSelectedObj({ id: "", name: "" });
    }
  };

  /* --------------------- handleNext (neu) --------------------- */
  const PRE_REG_STEP_NAMES = {
    1: "marke",
    2: "modell",
    3: "baujahr",
    4: "bauform",
    5: "kraftstoff",
    6: "variante",
    7: "ps",
    8: "getriebe",
    9: "tueren",
    10: "modelltyp",
    11: "kilometer",
  };

  const handleNext = async () => {
    const isSubmitStep = step >= 11;

    if (isAnimating && !isSubmitStep) return;

    setFilteredForStep([], step);

    if (
      userData &&
      Array.isArray(userData.fins) &&
      userData.fins.length >= 10
    ) {
      showToastDeferred(
        "Du hast die maximale Anzahl an Fahrzeugen in deinem Account erreicht. Entferne eines, um dieses Fahrzeug hinzuzufügen.",
        "error",
      );
      return;
    }

    if (isSubmitStep) {
      const km = String((selectedKmStand ?? "").toString().trim());
      if (!km) {
        showToastDeferred("Kein Kilometerstand angegeben", "error");
        return;
      }
      // Pre-reg tracking: km validated, guest only
      if (!userData) {
        setPreRegSourceFlow("homepage");
        trackPreReg(api, getPreRegSessionId(), "kilometer");
      }
      await handleManualAdd(km);
      return;
    }

    // Leeres/ungueltiges Feld -> Fehlermeldung am Input (Referenz-Stil), nicht weiter.
    // Gueltig, wenn die Auswahl eine id ODER einen name hat (der Flow nutzt fuer
    // manche Steps wie Variante den Namen statt der id) -> blockiert nichts Valides.
    const currentSelection = [
      selectedBrand,
      selectedModel,
      selectedYear,
      selectedBauform,
      selectedFuel,
      selectedVariant,
      selectedPs,
      selectedTransmission,
      selectedDoors,
      selectedModelType,
    ][step - 1];
    const currentValid = !!(
      currentSelection &&
      (currentSelection.id || currentSelection.name)
    );
    if (!currentValid) {
      setInputError(
        {
          1: "Bitte gib eine gültige Automarke ein",
          2: "Bitte gib ein gültiges Modell ein",
          3: "Bitte wähle ein Baujahr",
          4: "Bitte wähle eine Bauform",
          5: "Bitte wähle einen Kraftstoff",
          6: "Bitte wähle eine Variante",
          7: "Bitte wähle den PS-Wert",
          8: "Bitte wähle ein Getriebe",
          9: "Bitte wähle die Türenanzahl",
          10: "Bitte wähle einen Modeltyp",
        }[step] || "Bitte triff eine gültige Auswahl",
      );
      return;
    }

    // Normal steps → fade
    const targetPromise = computeNextStep(); // parallel
    handleFadeOut();
    const target = await targetPromise;

    if (target === "STOP" || target == null) {
      setAnimateOut(false);
      setIsAnimating(false);
      return;
    }

    // Pre-reg tracking: step confirmed, guest only
    if (!userData) {
      const preRegStepName = PRE_REG_STEP_NAMES[step];
      if (preRegStepName) {
        setPreRegSourceFlow("homepage");
        trackPreReg(api, getPreRegSessionId(), preRegStepName);
      }
    }

    // Ziel merken
    nextStepRef.current = target;

    // Sofort committen, falls Fade-Out schon durch ist (Edge)
    if (fadeOutDoneRef.current) {
      ensureFadeInFor(target);
    }
  };

  /* --------------------- handleBack (neu) --------------------- */
  const handleBack = async () => {
    if (isAnimating) return;
    setFilteredForStep([], step);
    if (step <= 1) return;

    fadeOutDoneRef.current = false;
    handleFadeOut();

    // Back-Ziel früh registrieren (Step 2 -> 1), damit shouldAnimateInfo bereits
    // stimmt und der Chip sauber raus-fadet, bevor getBackStep die Marke löscht.
    if (step === 2) nextStepRef.current = 1;

    const found = await vehicleFlow.getBackStep({
      step,
      selections: {
        selectedBrand,
        selectedModel,
        selectedYear,
        selectedBauform,
        selectedFuel,
        selectedVariant,
        selectedPs,
        selectedTransmission,
        selectedDoors,
        selectedModelType,
      },
    });

    nextStepRef.current = found;
    if (fadeOutDoneRef.current) {
      ensureFadeInFor(found);
    }
  };

  /* -------------------- Mappings / UI Helpers -------------------- */
  const allOptions = [
    brands,
    models,
    years,
    bauformen,
    fuels,
    variants,
    psOptions,
    gearboxOptions,
    doorOptions,
    modelTypeOptions,
    [],
  ];

  const allInputs = [
    brandInput,
    modelInput,
    yearInput,
    bauformInput,
    fuelInput,
    variantInput,
    psInput,
    transmissionInput,
    doorsInput,
    modelTypeInput,
    kmStandInput,
  ];

  const placeholders = useMemo(
    () => [
      "Audi, VW, BMW…",
      "A3, X5…",
      "2020",
      "Limousine, SUV…",
      "Diesel, Benzin…",
      "30 TDI…",
      "116",
      "Automatik, Schaltgetriebe",
      "Z. B. 3 oder 5",
      "Modelvariante",
      "Kilometerstand",
    ],
    [],
  );

  const preferredSamplesPerBrand = useMemo(
    () => ({ Volkswagen: ["Golf VII", "Polo"] }),
    [],
  );

  const dynamicPlaceholder = useMemo(() => {
    const makeSample = (arr, fallback, brandName) => {
      if (!arr?.length) return fallback;
      const prefs = preferredSamplesPerBrand[brandName];
      if (prefs) {
        const preferred = [];
        prefs.some((pref) => {
          const hit = arr.find(({ name }) =>
            name.toLowerCase().startsWith(pref.toLowerCase()),
          );
          if (hit) preferred.push(hit);
          return preferred.length === 2;
        });
        if (preferred.length === 2)
          return `${preferred.map((o) => o.name).join(", ")}…`;
        arr = [...preferred, ...arr.filter((o) => !preferred.includes(o))];
      }
      const alpha = arr.filter((o) => /^[A-Za-zÄÖÜäöü]/.test(o.name));
      const numeric = arr.filter((o) => !/^[A-Za-zÄÖÜäöü]/.test(o.name));
      const ordered = [...alpha, ...numeric];
      const seen = new Set();
      const uniques = ordered.filter(({ name }) => {
        const prefix = name.split(/\s+/)[0].toLowerCase();
        if (seen.has(prefix)) return false;
        seen.add(prefix);
        return true;
      });
      if (!uniques.length) return fallback;
      const pickSpread = (list, n = 2) => {
        if (list.length <= n) return list;
        const step = Math.floor(list.length / n);
        return Array.from(
          { length: n },
          (_, i) => list[Math.min(i * step, list.length - 1)],
        );
      };
      const samples = pickSpread(uniques, 2).map((o) => o.name);
      return `${samples.join(", ")}…`;
    };

    const brandName = selectedBrand?.name;
    switch (step) {
      case 2:
        return makeSample(models, "Modell…", brandName);
      case 6:
        return makeSample(variants, "Variante…", brandName);
      case 10:
        return makeSample(modelTypeOptions, "Modeltyp…", brandName);
      default:
        return placeholders[step - 1];
    }
  }, [
    step,
    models,
    variants,
    modelTypeOptions,
    selectedBrand?.name,
    placeholders,
    preferredSamplesPerBrand,
  ]);

  const inputTypes = [
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "text",
    "number",
  ];

  const { auction, status: carStatus } = carData ?? {};
  const isSalePhase =
    auction?.sellStatus === "price-confirmed" ||
    auction?.sellStatus === "sold" ||
    auction?.sellStatus === "cancelled";

  const isNotDone =
    carStatus !== "approved" &&
    carStatus !== "finished" &&
    carStatus !== "extended";

  const showTimer = !isSalePhase && isNotDone && !isSelfValuationFlow;

  const headers = [
    "",
    "Deine Automarke",
    "Dein Modell",
    "Baujahr",
    "Bauform",
    "Kraftstoff",
    "Variante",
    "PS-Wert",
    "Getriebe",
    "Türen",
    "Modeltyp",
    "Kilometerstand",
  ];

  const maxStep = headers.length - 1;
  const percentage =
    step > 0 ? (step === maxStep ? 99 : Math.round((step / maxStep) * 100)) : 0;

  const selectedChips = useMemo(() => {
    const arr = [];
    if (selectedBrand?.name)
      arr.push({ key: "brand", label: "Marke", value: selectedBrand.name });
    if (selectedModel?.name)
      arr.push({ key: "model", label: "Modell", value: selectedModel.name });
    if (selectedYear?.name)
      arr.push({ key: "year", label: "Baujahr", value: selectedYear.name });
    if (selectedBauform?.name)
      arr.push({ key: "bau", label: "Bauform", value: selectedBauform.name });
    if (selectedFuel?.name)
      arr.push({ key: "fuel", label: "Kraftstoff", value: selectedFuel.name });
    if (selectedVariant?.name)
      arr.push({ key: "var", label: "Variante", value: selectedVariant.name });
    if (selectedPs?.name)
      arr.push({ key: "ps", label: "PS", value: selectedPs.name });
    if (selectedTransmission?.name)
      arr.push({
        key: "gb",
        label: "Getriebe",
        value: selectedTransmission.name,
      });
    if (selectedDoors?.id)
      arr.push({
        key: "doors",
        label: "Türen",
        value: String(selectedDoors.id),
      });
    if (selectedModelType?.name)
      arr.push({ key: "mt", label: "Modeltyp", value: selectedModelType.name });
    if (selectedKmStand)
      arr.push({
        key: "km",
        label: "Kilometer",
        value: String(selectedKmStand),
      });
    return arr.slice(0, Math.max(0, step - 1));
  }, [
    step,
    selectedBrand?.name,
    selectedModel?.name,
    selectedYear?.name,
    selectedBauform?.name,
    selectedFuel?.name,
    selectedVariant?.name,
    selectedPs?.name,
    selectedTransmission?.name,
    selectedDoors?.id,
    selectedModelType?.name,
    selectedKmStand,
  ]);

  // Wichtig: Statt pendingTargetStep nutzen wir das Ref
  const shouldAnimateInfo = step === 1 || nextStepRef.current === 1;

  return (
    <section className="hero-secondary">
      {(carData || loading) &&
      !addVehicle &&
      userData &&
      step < 12 &&
      !isChevronAnimating ? (
        <div className="section-container-home-secondary">
          <div
            className="Car-Top-secondary latest"
            style={{
              height: step === 12 ? "100px" : undefined,
              display: isMobile && step === 12 ? "none" : "flex",
            }}
          >
            {/* Desktop-only: Fahrzeug-Bild rechts (wie im Referenzdesign) */}
            <Image
              src={heroCar}
              alt=""
              aria-hidden="true"
              className="home-hero-car"
              sizes="700px"
            />

            <div className="Car-Top-wrapper">
              <div className="Car-Top-main-wrapper" style={{ width: "60%" }}>
                <h1
                  className="Car-Top-title secondary"
                  style={{ lineHeight: 1.2 }}
                >
                  Willkommen zurück, <br /> {userData?.vorname}
                </h1>

                <div className="Car-Top-Info Car-Top-Info--welcome-copy">
                  <p className="Car-Top-Info-copy-mobile">
                    Erhalte das beste Angebot von über 5.000 Händlern
                  </p>
                  <p className="Car-Top-Info-copy-desktop">
                    Erhalte das beste Angebot von über 5.000 Händlern -
                    inklusive kostenloser Abholung und Bezahlung am selben Tag.
                  </p>
                </div>

                <div className="welcome-trustpilot-desktop">
                  <TrustPilot />
                </div>
              </div>

              <div className="Car-top-vehicle">
                <div className="Car-top-vehicle-wrapper">
                  {loading ? (
                    <Skeleton
                      width={70}
                      height={70}
                      containerClassName="skeleton"
                    />
                  ) : (
                    <BrandLogo brand={carData?.marke} reserveSpace />
                  )}

                  <div className="vehicle-main-price-wrapper">
                    <div className="vehicle-price-wrapper">
                      {loading ? (
                        <Skeleton
                          width={200}
                          height={38}
                          containerClassName="skeleton vehicle-price-main-skeleton"
                        />
                      ) : (
                        <>
                          {/* Referenz-Style: Preis steht bereits im HTML, das
                              Blur-Overlay "enthüllt" ihn nach der Hydration. */}
                          <div className="vehicle-price-reveal">
                            <p className="vehicle-predicted-price">
                              {(() => {
                              if (!effectiveIsActiveNotExpired) {
                                if (hasPrice)
                                  return `${formatNumber(displayEstimate)}€`;
                                if (hasMinPrice)
                                  return `${formatNumber(carData?.minPrice)}€`;
                                return "—";
                              }

                              if (hasFinalPrice) {
                                return `${formatNumber(
                                  carData?.auction?.finalPrice,
                                )}€`;
                              }

                              if (isSelfValuationFlow) {
                                if (hasMinPrice) {
                                  return `${formatNumber(carData?.minPrice)}€`;
                                }
                                if (hasPrice) {
                                  return `${formatNumber(displayEstimate)}€`;
                                }
                                return `${computedProgress}%`;
                              }

                              if (isPriceCalculationAutomated) {
                                if (hasMinPrice || hasPrice) {
                                  const value = hasMinPrice
                                    ? carData?.minPrice
                                    : displayEstimate;
                                  return `${formatNumber(value)}€`;
                                }
                                if (waitingForValuation) {
                                  return "60 Minuten";
                                }
                                return `${computedProgress}%`;
                              }

                              const priceValue = hasMinPrice
                                ? carData?.minPrice
                                : displayEstimate;
                              if (
                                priceValue === undefined ||
                                priceValue === null ||
                                String(priceValue).trim() === ""
                              ) {
                                return "—";
                              }
                                return `${formatNumber(priceValue)}€`;
                              })()}
                            </p>
                            <span
                              className={`vehicle-price-reveal-overlay${
                                priceRevealed ? " is-revealed" : ""
                              }`}
                              aria-hidden="true"
                            >
                              <LoadingAnimation />
                            </span>
                          </div>
                          <span
                            className="vehicle-price-tooltip skeleton-end-fade-in"
                            aria-label={vehiclePriceTooltipText}
                          >
                            <Image
                              src={questionMarkIcon}
                              alt="Info zum Fahrzeugpreis"
                              className="vehicle-price-question"
                            />
                            <span
                              className="vehicle-price-tooltip-bubble"
                              role="tooltip"
                            >
                              {vehiclePriceTooltipText}
                            </span>
                          </span>
                        </>
                      )}
                    </div>

                    {loading ? (
                      <Skeleton
                        width={100}
                        height={18}
                        containerClassName="skeleton vehicle-price-subtitle-skeleton"
                      />
                    ) : (
                      <div className="vehicle-price-subtitle skeleton-end-fade-in">
                        {(() => {
                          if (isSelfValuationFlow) {
                            if (hasFinalPrice) return "Höchstes Angebot";
                            if (hasMinPrice) return "Dein Richtpreis";
                            if (hasPrice) return "Geschätzter Preis";
                            return "Fertiggestellt";
                          }

                          if (isPriceCalculationAutomated) {
                            if (!effectiveIsActiveNotExpired)
                              return "Bewertung abgelaufen";
                            if (hasFinalPrice) return "Höchstes Angebot";
                            if (hasMinPrice) return "Richtpreis";
                            if (hasPrice) return "Geschätzter Preis";
                            if (waitingForValuation) return "Geschätzte Dauer";
                            return "Fertiggestellt";
                          }
                          if (!effectiveIsActiveNotExpired)
                            return "Bewertung abgelaufen";
                          if (hasFinalPrice) return "Höchstes Angebot";
                          return hasMinPrice
                            ? "Richtpreis"
                            : "Geschätzter Preis";
                        })()}
                      </div>
                    )}
                  </div>

                  {loading ? (
                    <Skeleton
                      width={200}
                      height={24}
                      containerClassName="skeleton"
                    />
                  ) : (
                    <span className="vehicle-brand-model">
                      {carData?.marke ?? ""} {carData?.model ?? ""}
                    </span>
                  )}

                  {loading ? (
                    <Skeleton
                      width={170}
                      height={18}
                      containerClassName="skeleton vehicle-details-skeleton"
                    />
                  ) : (
                    <ul className="vehicle-details">
                      <li>{carData?.baujahr ?? ""}</li>
                      <li>
                        {carData?.kmStand
                          ? `${formatNumber(carData.kmStand)}km`
                          : ""}
                      </li>
                      <li>{carData?.fueltype ?? ""}</li>
                    </ul>
                  )}

                  {!loading && showTimer && !showPercentage ? (
                    <div
                      className={`timer ${
                        timeLeft?.trim() === "Deine Bewertung ist abgelaufen"
                          ? "expired"
                          : ""
                      }`}
                    >
                      <Image
                        src={
                          timeLeft?.trim() === "Deine Bewertung ist abgelaufen"
                            ? attentionIcon
                            : timerIcon
                        }
                        alt="Autoeinfachlos - Auto einfach online verkaufen"
                        className="timer-icon"
                      />
                      <span>
                        {/* Zeitbasierter Text: ms-Differenz zwischen Server-
                            und Client-Uhr darf keinen Hydration-Error werfen */}
                        <strong suppressHydrationWarning>{timeLeft}</strong>
                      </span>
                    </div>
                  ) : null}

                  {/* BUTTON: skeleton while loading, otherwise button */}
                  {loading ? (
                    <Skeleton
                      height={48}
                      width="100%"
                      containerClassName="skeleton vehicle-main-button-skeleton"
                    />
                  ) : (
                    <ButtonAnimated
                      height="48px"
                      width="100%"
                      fontSize="18px"
                      text={isMobile ? "Weiter" : buttonTitle}
                      link={buttonLink}
                      loadingOnClick
                    />
                  )}
                </div>

                <ButtonAnimated
                  width="100%"
                  height="48px"
                  className="welcome-add-vehicle"
                  hideChevron={true}
                  backgroundColor="transparent"
                  color="#1d1d1b"
                  hoverBackgroundColor="#e6ca44"
                  hoverColor="#1d1d1b"
                  text="+ Weiteres Auto hinzufügen"
                  fontSize="18px"
                  onClick={() => setAddVehicle(true)}
                />

                <div className="welcome-trustpilot-mobile">
                  <TrustPilot />
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        step < 12 && (
          <div className="section-container-home-secondary">
            <div className="Car-Top-Animation">
              <div className="Car-Top-Cars">
                <div className="Car-Top-Cars">
                  <Image
                    src={blackCar}
                    className="Car-Top-blackCar"
                    alt="Autoeinfachlos - Auto einfach online verkaufen"
                    sizes="(max-width: 768px) 340px, 500px"
                  />
                  <Image
                    src={whiteCar}
                    className="Car-Top-whiteCar"
                    alt="Autoeinfachlos - Auto einfach online verkaufen"
                    sizes="(max-width: 768px) 220px, 452px"
                    quality={65}
                  />
                </div>
              </div>
              <div
                className={`Car-Top-Confetti ${
                  isChevronAnimating ? "animate" : ""
                } ${lockFadeIn ? "is-hidden" : ""}`}
              >
                {/* Träger des gekachelten Mobil-Confetti (::before/::after) */}
                <div className="confetti-wrapper" />
                <Konfetti className="Konfetti" />
                <Konfetti2 className="Konfetti2" />
              </div>
              <div
                className={`Chevron-Animation-first ${
                  isChevronAnimating
                    ? `animate${isChevronLooping ? " loop" : ""}`
                    : ""
                }`}
              />
              <div
                className={`Chevron-Animation ${
                  isChevronAnimating
                    ? `animate delay-1${isChevronLooping ? " loop" : ""}`
                    : ""
                }`}
              />
              <div
                className={`Chevron-Animation ${
                  isChevronAnimating
                    ? `animate delay-2${isChevronLooping ? " loop" : ""}`
                    : ""
                }`}
              />
              <div
                className={`Chevron-Animation ${
                  isChevronAnimating
                    ? `animate delay-3${isChevronLooping ? " loop" : ""}`
                    : ""
                }`}
              />
              <div
                className={`Chevron-Animation-last ${
                  isChevronAnimating
                    ? `animate delay-4${isChevronLooping ? " loop" : ""}`
                    : ""
                }`}
                onAnimationEnd={handleChevronAnimationEnd}
              />
            </div>

            <div
              className={`Car-Top-secondary`}
              style={{
                height: step === 12 ? "100px" : undefined,
                display: isMobile && step === 12 ? "none" : "flex",
              }}
            >
              <div
                className={`Car-Top-wrapper ${
                  isChevronAnimating ? "animate" : ""
                } ${lockFadeIn ? "is-hidden" : ""}`}
              >
                <div className="Car-Top-main-wrapper">
                  {/* Render both variants and toggle via CSS media query to avoid hydration reflow/CLS. */}
                  <h1 className="Car-Top-title" style={{ lineHeight: 1.2 }}>
                    <span className="Car-Top-title-variant Car-Top-title-variant--desktop">
                      Verkaufe dein Auto&nbsp;
                      <br />
                      zum&nbsp;
                      <Underline color="white" text="Bestpreis" />
                    </span>
                    <span className="Car-Top-title-variant Car-Top-title-variant--mobile">
                      Verkaufe dein Auto <br />
                      einfach zum <br />
                      <Underline color="white" text="Bestpreis" />
                    </span>
                  </h1>

                  <CarTopInfo
                    items={selectedChips}
                    textNode={
                      <p>
                        <strong>
                          Erhalte das beste Angebot von über 5.000 Händlern
                        </strong>{" "}
                        - inklusive kostenloser Abholung und Bezahlung am
                        selben Tag.
                      </p>
                    }
                    shouldAnimate={shouldAnimateInfo}
                    animateOut={animateOut}
                    animateIn={animateIn}
                    // versteckt während Swap/Submit UND im Gap zwischen Fade-Out
                    // und Fade-In (sonst rendert der Subtitle dort kurz bei voller
                    // Deckkraft -> "kommt -> weg -> zurück"-Flackern beim Zurück)
                    hidden={
                      swapInvisible ||
                      lockFadeIn ||
                      (isAnimating && !animateOut && !animateIn)
                    }
                  />

                  <div
                    key={`step-${step}`} // 👈 zwingt neuen DOM-Knoten pro Step
                    className={`Car-Top-Input-Wrapper-main
    ${animateOut ? "fade-out" : ""}
                    ${animateIn ? "fade-in" : ""}
    ${swapInvisible || lockFadeIn ? "is-hidden" : ""}`}
                    onAnimationEnd={
                      animateOut ? onFadeOutEnd : animateIn ? onFadeInEnd : null
                    }
                  >
                    <p className="Car-top-FIN desktop">
                      <strong>{headers[step]}</strong>&nbsp;
                      {step > 1 && (
                        <>
                          <span className="step-percentage">
                            ({percentage}% abgeschlossen)
                          </span>
                          <button type="button" className="back-button" onClick={handleBack}>
                            Zurück
                          </button>
                        </>
                      )}
                    </p>

                    <div className="Car-Top-Input-wrapper">
                      <p className="Car-top-FIN mobile">
                        <strong>{headers[step]}</strong>&nbsp;
                        {step > 1 && (
                          <>
                            <span className="step-percentage">
                              ({percentage}% abgeschlossen)
                            </span>
                            <button type="button" className="back-button" onClick={handleBack}>
                              Zurück
                            </button>
                          </>
                        )}
                      </p>

                      <div className="Car-top-input-values">
                        <Input
                          key={`input-${step}`} // ← new node per step
                          type={inputTypes[step - 1]}
                          value={allInputs[step - 1]}
                          placeholder={dynamicPlaceholder}
                          onFocus={() => {
                            setInputError("");
                            setFilteredForStep(allOptions[step - 1], step);
                          }}
                          valid={
                            !![
                              selectedBrand.id,
                              selectedModel.id,
                              selectedYear.id,
                              selectedBauform.id,
                              selectedFuel.id,
                              selectedVariant.id,
                              selectedPs.id,
                              selectedTransmission.id,
                              selectedDoors.id,
                              selectedModelType.id,
                              selectedKmStand,
                            ][step - 1]
                          }
                          onBlur={() => setFilteredForStep([], step)}
                          onChange={(e) => {
                            setInputError("");
                            handleInput(
                              e.target.value,
                              allOptions[step - 1],
                              allSetInputs[step - 1],
                              (allFiltered) =>
                                setFilteredForStep(allFiltered, step),
                              allSetSelected[step - 1],
                              step,
                            );
                          }}
                          inputMode={
                            step === 3 || step === 11 ? "numeric" : undefined
                          }
                          pattern={
                            step === 3 || step === 11 ? "[0-9]*" : undefined
                          }
                          errorMessage={inputError}
                        />

                        {filteredItems.length > 0 &&
                          filteredItemsStep === step && (
                            <div className="Input-dropdown-shell">
                              <ul className="Input-dropdown-values">
                                {filteredItems.map((o, i) => (
                                <li
                                  key={i}
                                  className="Input-dropdown-value"
                                  onMouseDown={() =>
                                    handleSelect(
                                      o,
                                      allSetInputs[step - 1],
                                      allSetSelected[step - 1],
                                      (items) =>
                                        setFilteredForStep(items, step),
                                    )
                                  }
                                >
                                  {o.name}
                                </li>
                              ))}
                              </ul>
                            </div>
                          )}
                      </div>

                      {/* statt zwei immer-gleichzeitig gerenderten Buttons */}
                      {isMobile ? (
                        <ButtonAnimated
                          onClick={handleNext}
                          className="HomePageTopButton mobile"
                          tooltipPosition="bottom"
                          text={"Weiter"}
                        />
                      ) : (
                        <ButtonAnimated
                          onPointerDown={handleNext}
                          className="HomePageTopButton desktop"
                          tooltipPosition="top"
                          text={"Weiter"}
                        />
                      )}
                    </div>
                  </div>

                  <TrustPilot />
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </section>
  );
};
